
import React, { useState, useEffect, useMemo } from 'react';
import { SYS_API_BASE } from '../constants/calendar';
import {
    Box, Typography, Grid, Paper, TextField, Button, IconButton, List, ListItem, ListItemButton,
    ListItemText, ListItemSecondaryAction, Divider, Card, CardContent,
    Autocomplete, Checkbox, FormControlLabel, CircularProgress, Alert,
    Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
    Tooltip, Chip, Tab, Tabs, Snackbar, Dialog, DialogTitle, DialogContent, DialogActions,
    useMediaQuery
} from '@mui/material';

// @ts-ignore - suppress unused import warnings for MUI icons
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import SendIcon from '@mui/icons-material/Send';
import SaveIcon from '@mui/icons-material/Save';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PreviewIcon from '@mui/icons-material/Preview';
import EditIcon from '@mui/icons-material/Edit';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import dayjs from 'dayjs';
import { fetchDispatchReservations } from '../api/dispatchService';
import DashboardSidebar from '../components/DashboardSidebar';

const BulkMail: React.FC = () => {
    const [recipients, setRecipients] = useState<any[]>([]);
    const [template, setTemplate] = useState<{ subject: string; body: string }>({ subject: '', body: '' });
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [targetDate, setTargetDate] = useState(dayjs().format('YYYY-MM-DD'));
    const [status, setStatus] = useState<{ loading: boolean; message: string; type: string }>({ loading: false, message: '', type: 'info' });
    const [templateDialog, setTemplateDialog] = useState<{ open: boolean; list: any[]; loading: boolean }>({ open: false, list: [], loading: false });
    const [nameEditModal, setNameEditModal] = useState<{ open: boolean; id: any; name: string }>({ open: false, id: null, name: '' });
    const [taskStatus, setTaskStatus] = useState<any>({
        open: false,
        message: '',
        severity: 'info',
        duration: 6000,
        sending: false,
        current: 0,
        total: 0
    });
    const [tabValue, setTabValue] = useState(0);
    const [preventDuplicates, setPreventDuplicates] = useState(true);
    const [currentTemplateName, setCurrentTemplateName] = useState('');

    const VALID_STATUSES = ['予約確定', 'オファー中', '要連絡/未確定', '保留中', 'リクエスト'];

    const sysApiBase = SYS_API_BASE;
    // テンプレート変数のパース
    const parseTemplate = (text: string, data: any): string => {
        if (!text) return '';
        let result = text;

        // 変数置換 {key}
        // data内のすべてのキーをループして置換
        const flattenData = {
            ...data,
            ...data.detail,
            customer_name: data.customer_name || data.detail?.name || '',
            customer_email: data.customer_email || data.detail?.mail || '',
            start_date: data.start_date ? dayjs(data.start_date).format('YYYY/MM/DD HH:mm') : '',
            start_date_mmdd: data.start_date ? dayjs(data.start_date).format('M/D') : '',
            end_date: data.end_date ? dayjs(data.end_date).format('YYYY/MM/DD HH:mm') : '',
            sum_price: data.sum_price || data.detail?.sum_price || 0,
            line: data.detail?.line ? 1 : 0,
            whatsapp: data.detail?.whatsapp ? 1 : 0,
            instagram: data.detail?.instagram ? 1 : 0,
            sms: data.detail?.sms ? 1 : 0,
        };

        Object.keys(flattenData).forEach(key => {
            const val = flattenData[key];
            const placeholder = new RegExp(`\\{${key}\\}`, 'g');
            result = result.replace(placeholder, val !== null && val !== undefined ? val : '');
        });

        // if文の簡易処理 {% if condition %} body {% endif %}
        // 対応演算子: ==, !=, >, <, >=, <=
        const ifRegex = /\{% if (.*?) %\}([\s\S]*?)\{% endif %\}/g;
        result = result.replace(ifRegex, (match, condition, body) => {
            try {
                // 条件式の解析
                const parts = condition.trim().split(/\s+/);
                if (parts.length === 1) {
                    // 単体キーの存在チェック (0 or false なら偽)
                    const val = flattenData[parts[0]];
                    return (val && val !== '0' && val !== 0) ? body : '';
                }

                if (parts.length === 3) {
                    const [key, op, compareVal] = parts;
                    const left = flattenData[key];
                    let right = compareVal.replace(/['"]/g, ''); // 引用符除去

                    // 数値に変換可能なら変換
                    const leftNum = Number(left);
                    const rightNum = Number(right);
                    const isNum = !isNaN(leftNum) && !isNaN(rightNum);

                    const l = isNum ? leftNum : String(left);
                    const r = isNum ? rightNum : String(right);

                    let isTrue = false;
                    switch (op) {
                        case '==': isTrue = l == r; break;
                        case '!=': isTrue = l != r; break;
                        case '>': isTrue = l > r; break;
                        case '<': isTrue = l < r; break;
                        case '>=': isTrue = l >= r; break;
                        case '<=': isTrue = l <= r; break;
                        default: isTrue = false;
                    }
                    return isTrue ? body : '';
                }
                return '';
            } catch (e) {
                console.error('Template Parse Error:', e);
                return match;
            }
        });

        return result;
    };

    // 予約検索
    const handleSearch = async () => {
        if (searchQuery.length < 2) return;
        setLoading(true);
        try {
            const data = await fetchDispatchReservations({ name: searchQuery });
            setSearchResults(data || []);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    // リスト取得 (貸出/返却)
    const fetchList = async (type: string) => {
        setLoading(true);
        try {
            const params = type === 'pickup'
                ? { start_date: targetDate, end_date: targetDate }
                : { start_date_end: targetDate, end_date_start: targetDate }; // 実際のエンドポイントに合わせる必要があるが、とりあえず全取得してフィルタ

            const data = await fetchDispatchReservations({
                start_date: dayjs(targetDate).subtract(1, 'day').format('YYYY-MM-DD'),
                end_date: dayjs(targetDate).add(1, 'day').format('YYYY-MM-DD')
            });

            const filtered = data.filter(r => {
                const date = type === 'pickup' ? r.start_date : r.end_date;
                const isTargetDate = dayjs(date).format('YYYY-MM-DD') === targetDate;
                // ステータスフィルタ
                const hasValidStatus = VALID_STATUSES.includes(r.status_name) || VALID_STATUSES.includes(r.status);
                return isTargetDate && hasValidStatus;
            });

            // 追加予定のリストを作成
            const newRecipients = [...recipients];
            filtered.forEach(r => {
                // ID重複チェック
                if (newRecipients.find(x => x.id === r.id)) return;

                // メールアドレス重複チェック (有効な場合)
                if (preventDuplicates) {
                    const email = (r.customer_email || (r.detail as any)?.mail || '').toLowerCase().trim();
                    if (email && newRecipients.find(x => (x.customer_email || (x.detail as any)?.mail || '').toLowerCase().trim() === email)) {
                        return;
                    }
                }

                newRecipients.push(r);
            });
            setRecipients(newRecipients);
            setStatus({ loading: false, message: `${filtered.length}件追加しました`, type: 'success' });
        } catch (error) {
            setStatus({ loading: false, message: 'リスト取得に失敗しました', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const addRecipient = (res: any) => {
        // ID重複チェック
        if (recipients.find(r => r.id === res.id)) {
            setSearchResults([]);
            setSearchQuery('');
            return;
        }

        // メールアドレス重複チェック
        if (preventDuplicates) {
            const email = (res.customer_email || res.detail?.mail || '').toLowerCase().trim();
            if (email && recipients.find(r => (r.customer_email || r.detail?.mail || '').toLowerCase().trim() === email)) {
                alert('このメールアドレスは既にリストに存在します（重複を許可しない設定になっています）');
                return;
            }
        }

        setRecipients([...recipients, res]);
        setSearchResults([]);
        setSearchQuery('');
    };

    const removeRecipient = (id: any) => {
        setRecipients(recipients.filter(r => r.id !== id));
    };

    // テンプレート保存/読み込み
    const saveTemplate = async (asNew: boolean = false) => {
        let name = currentTemplateName;

        if (asNew || !name) {
            name = prompt('新しいテンプレート名を入力してください', asNew ? `${name}_copy` : '');
            if (!name) return;
        } else {
            if (!window.confirm(`テンプレート「${name}」を上書き保存してもよろしいですか？`)) return;
        }

        const url = `${sysApiBase}/mail_templates.php`.replace(/\/+/g, '/'); // 重複スラッシュ防止
        const finalUrl = url.startsWith('http') ? url : window.location.origin + (url.startsWith('/') ? url : '/' + url);

        try {
            const res = await fetch(finalUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'save', name, ...template })
            });
            const text = await res.text();
            try {
                const data = JSON.parse(text);
                if (data.success) {
                    alert('保存しました');
                    setCurrentTemplateName(name);
                } else {
                    alert('保存に失敗しました: ' + (data.message || 'Unknown error'));
                }
            } catch (jsonErr) {
                alert(`APIからの応答がJSONではありません。\nURL: ${finalUrl}\n応答の冒頭: ${text.substring(0, 100)}`);
            }
        } catch (e) {
            alert('通信に失敗しました: ' + e.message);
        }
    };

    const handleCleanNames = () => {
        const cleaned = recipients.map(r => {
            let name = r.customer_name || '';
            // 1. 括弧内を除去 (半角・全角)
            name = name.replace(/[\(\（].*?[\)\）]/g, '');
            // 2. ｰ①, ｰ②, -1, -2 などの枝番パターンを末尾から削除
            name = name.replace(/[ｰ\-\－\─][①-⑨\d]+$/g, '');
            // 3. 末尾に残った記号や空白を掃除
            name = name.replace(/[ｰ\-\－\─\s]+$/g, '');
            return { ...r, customer_name: name.trim() };
        });
        setRecipients(cleaned);
        setStatus({ loading: false, message: '名前をクリーニングしました', type: 'success' });
    };

    const updateRecipientName = () => {
        if (!nameEditModal.id) return;
        setRecipients(recipients.map(r =>
            r.id === nameEditModal.id ? { ...r, customer_name: nameEditModal.name } : r
        ));
        setNameEditModal({ open: false, id: null, name: '' });
    };

    const openTemplateDialog = async () => {
        setTemplateDialog({ ...templateDialog, open: true, loading: true });
        try {
            const res = await fetch(`${sysApiBase}/mail_templates.php`);
            const data = await res.json();
            if (data.success) {
                setTemplateDialog({ open: true, list: data.list || [], loading: false });
            } else {
                setTemplateDialog(prev => ({ ...prev, loading: false }));
                alert('テンプレート一覧の取得に失敗しました');
            }
        } catch (e) {
            setTemplateDialog(prev => ({ ...prev, loading: false }));
            alert('通信エラーが発生しました');
        }
    };

    const loadTemplate = async (name: string) => {
        try {
            const res = await fetch(`${sysApiBase}/mail_templates.php?name=${encodeURIComponent(name)}`);
            const data = await res.json();
            if (data.success) {
                setTemplate({ subject: data.subject, body: data.body });
                setCurrentTemplateName(name);
                setTemplateDialog(prev => ({ ...prev, open: false }));
            } else {
                alert('テンプレートが見つかりません');
            }
        } catch (e) {
            alert('読み込みに失敗しました');
        }
    };

    const deleteTemplate = async (name: string) => {
        if (!window.confirm(`テンプレート「${name}」を削除しますか？`)) return;
        try {
            // 現状のAPIはDELETEアクションがないため、必要に応じて追加検討。
            // ひとまず仕様外だが、必要ならPHP側も修正する。
            // ここではフロントエンドのみの実装とするが、ユーザーの要望に応じて後ほどPHP側も拡張可能。
            alert('削除機能は現在メンテナンス中です');
        } catch (e) {
            alert('削除に失敗しました');
        }
    };

    // メール送信実行
    const handleSend = async () => {
        if (recipients.length === 0) return alert('宛先がいません');
        if (!template.subject || !template.body) return alert('テンプレートを入力してください');
        if (!window.confirm(`${recipients.length}件のメールを送信します。よろしいですか？`)) return;

        // 同時送信の設定（ここを10に変えれば10通ずつ並列送信されます）
        const CONCURRENCY = 5;
        const totalOriginal = recipients.length;

        // 事前に重複を除外（送信処理の並列化のために重要）
        const uniqueRecipients = [];
        const seenEmails = new Set();
        let skippedCount = 0;

        recipients.forEach(r => {
            const email = (r.customer_email || r.detail?.mail || '').toLowerCase().trim();
            if (email && seenEmails.has(email)) {
                skippedCount++;
            } else {
                uniqueRecipients.push(r);
                if (email) seenEmails.add(email);
            }
        });

        let successCount = 0;
        let failCount = 0;
        let currentProcessed = 0;

        setTaskStatus({
            open: true,
            message: `送信準備中... (0 / ${totalOriginal})`,
            severity: 'info',
            duration: null,
            sending: true,
            current: 0,
            total: totalOriginal
        });

        // 非同期・並列処理開始
        (async () => {
            // uniqueRecipientsをCONCURRENCYずつの塊（チャンク）に分けて処理
            for (let i = 0; i < uniqueRecipients.length; i += CONCURRENCY) {
                const chunk = uniqueRecipients.slice(i, i + CONCURRENCY);

                // チャンク内の送信を並列実行
                await Promise.all(chunk.map(async (r) => {
                    const parsedSubject = parseTemplate(template.subject, r);
                    const parsedBody = parseTemplate(template.body, r);
                    const email = r.customer_email || r.detail?.mail;

                    if (!email) {
                        failCount++;
                    } else {
                        try {
                            const res = await fetch(`${sysApiBase}/bulk_send.php`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                    to: email,
                                    subject: parsedSubject,
                                    body: parsedBody
                                })
                            });
                            const data = await res.json();
                            if (data.success) {
                                successCount++;
                            } else {
                                failCount++;
                            }
                        } catch (e) {
                            failCount++;
                        }
                    }

                    // 1件完了するごとに進捗を更新
                    currentProcessed++;
                    setTaskStatus(prev => ({
                        ...prev,
                        message: `メール送信中... (${currentProcessed + skippedCount} / ${totalOriginal})`,
                        current: currentProcessed + skippedCount
                    }));
                }));
            }

            // 完了通知
            setTaskStatus({
                open: true,
                message: `送信完了: 成功 ${successCount}件, 失敗 ${failCount}件${skippedCount > 0 ? `, 重複スキップ ${skippedCount}件` : ''}`,
                severity: failCount === 0 ? 'success' : 'warning',
                duration: 10000,
                sending: false,
                current: totalOriginal,
                total: totalOriginal
            });
        })();
    };

    useEffect(() => {
        document.title = 'メール一斉送信 | レンタカー予約カレンダー';
    }, []);

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', background: '#f8fafc' }}>
            <DashboardSidebar />

            <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, md: 4 }, pt: { xs: 7, md: 4 }, minWidth: 0 }}>
                <Box sx={{ mb: { xs: 2, md: 4 } }}>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b', mb: 1 }}>
                        メール一斉送信
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                        個別のカスタマーへメールを一斉送信します（To指定送信）
                    </Typography>
                </Box>

                {status.message && (
                    <Alert severity={status.type as any} sx={{ mb: 3 }} onClose={() => setStatus({ ...status, message: '' })}>
                        {status.message}
                    </Alert>
                )}

                <Grid container spacing={{ xs: 2, md: 4 }}>
                    {/* 宛先 / 選択済みリスト セクション */}
                    <Grid item xs={12} {...{} as any}>
                        <Paper sx={{
                            p: { xs: 2, md: 3 },
                            borderRadius: { xs: 2, md: 4 },
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            gap: { xs: 2, md: 4 },
                            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)'
                        }}>
                            {/* 左側（または上部）: 宛先操作 */}
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <PersonAddIcon color="primary" /> 宛先
                                    </Typography>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={preventDuplicates}
                                                onChange={(e) => setPreventDuplicates(e.target.checked)}
                                            />
                                        }
                                        label={<Typography variant="caption">重複メールを除外</Typography>}
                                        sx={{ mr: 0 }}
                                    />
                                </Box>

                                <Box sx={{ mb: 3 }}>
                                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="名前で検索..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                        />
                                        <Button variant="contained" onClick={handleSearch} disabled={loading}>
                                            <SearchIcon />
                                        </Button>
                                    </Box>
                                    {searchResults.length > 0 && (
                                        <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto', mt: 1 }}>
                                            <List {...{size: "small"} as any}>
                                                {searchResults.map(res => (
                                                    <ListItem key={res.id} disablePadding>
                                                        <ListItemButton onClick={() => addRecipient(res)}>
                                                            <ListItemText
                                                                primary={res.customer_name}
                                                                secondary={`${res.contact_code} | ${res.customer_email || 'アドレスなし'}`}
                                                            />
                                                        </ListItemButton>
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Paper>
                                    )}
                                </Box>

                                <Divider sx={{ mb: 3 }} />

                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>日別リストから追加</Typography>
                                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                        <TextField
                                            type="date"
                                            size="small"
                                            fullWidth
                                            value={targetDate}
                                            onChange={(e) => setTargetDate(e.target.value)}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button fullWidth variant="outlined" size="small" onClick={() => fetchList('pickup')} startIcon={<CalendarTodayIcon />}>
                                            当日貸出
                                        </Button>
                                        <Button fullWidth variant="outlined" size="small" onClick={() => fetchList('return')} startIcon={<CalendarTodayIcon />}>
                                            当日返却
                                        </Button>
                                    </Box>
                                </Box>

                                {/* 早期送信ボタン（リストが長い場合の利便性） */}
                                <Box sx={{ mt: 'auto', pt: 2 }}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        color="primary"
                                        size="large"
                                        startIcon={<SendIcon />}
                                        onClick={handleSend}
                                        disabled={recipients.length === 0}
                                        sx={{ borderRadius: 2, py: 1.5, fontWeight: 700 }}
                                    >
                                        一斉送信を開始
                                    </Button>
                                </Box>
                            </Box>

                            <Divider orientation="vertical" flexItem />

                            {/* 右側（下部）: 選択済み宛先リスト */}
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                        宛先リスト ({recipients.length})
                                    </Typography>
                                    {recipients.length > 0 && (
                                        <Tooltip title="名前から「-1」「ｰ①」などを自動削除">
                                            <Button
                                                size="small"
                                                startIcon={<AutoFixHighIcon />}
                                                onClick={handleCleanNames}
                                                sx={{ fontSize: '0.75rem' }}
                                            >
                                                名前を整える
                                            </Button>
                                        </Tooltip>
                                    )}
                                </Box>

                                <Box sx={{ flexGrow: 1, overflow: 'auto', maxHeight: 500 }}>
                                    <List dense>
                                        {recipients.map(r => (
                                            <ListItem key={r.id} divider>
                                                <ListItemText
                                                    primary={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            {r.customer_name}
                                                            <Chip
                                                                label={r.status_name || r.status}
                                                                size="small"
                                                                sx={{ height: 16, fontSize: '0.65rem', bgcolor: '#f1f5f9' }}
                                                            />
                                                        </Box>
                                                    }
                                                    secondary={r.customer_email || 'メールアドレス不明'}
                                                    secondaryTypographyProps={{
                                                        color: r.customer_email ? 'textSecondary' : 'error',
                                                        sx: { fontSize: '0.75rem' }
                                                    }}
                                                />
                                                <ListItemSecondaryAction>
                                                    <IconButton
                                                        edge="end"
                                                        size="small"
                                                        onClick={() => setNameEditModal({ open: true, id: r.id, name: r.customer_name })}
                                                        sx={{ mr: 0.5 }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton edge="end" size="small" onClick={() => removeRecipient(r.id)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                        ))}
                                        {recipients.length === 0 && (
                                            <Typography variant="body2" sx={{ textAlign: 'center', py: 4, color: '#94a3b8' }}>
                                                宛先が追加されていません
                                            </Typography>
                                        )}
                                    </List>
                                </Box>
                                {recipients.length > 0 && (
                                    <Button fullWidth color="error" startIcon={<DeleteIcon />} sx={{ mt: 2 }} onClick={() => setRecipients([])}>
                                        リストをクリア
                                    </Button>
                                )}
                            </Box>
                        </Paper>
                    </Grid>

                    {/* 下側: テンプレートとプレビュー */}
                    <Grid item xs={12} sx={{ mt: { xs: 1, md: 4 } }} {...{} as any}>
                        <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: { xs: 2, md: 4 } }}>
                            <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 3 }}>
                                <Tab label="エディタ" icon={<FolderOpenIcon />} iconPosition="start" />
                                <Tab label="プレビュー表示" icon={<PreviewIcon />} iconPosition="start" disabled={recipients.length === 0} />
                            </Tabs>

                            {tabValue === 0 ? (
                                <Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="h6" sx={{ fontWeight: 700 }}>本文・テンプレート作成</Typography>
                                            {currentTemplateName && (
                                                <Chip
                                                    label={currentTemplateName}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                    onDelete={() => {
                                                        setCurrentTemplateName('');
                                                        setTemplate({ subject: '', body: '' });
                                                    }}
                                                    sx={{ ml: 1 }}
                                                />
                                            )}
                                        </Box>
                                        <Box>
                                            <Button size="small" startIcon={<FolderOpenIcon />} onClick={openTemplateDialog}>開く</Button>
                                            {currentTemplateName && (
                                                <Button size="small" startIcon={<SaveIcon />} onClick={() => saveTemplate(false)} sx={{ ml: 1 }}>上書き</Button>
                                            )}
                                            <Button size="small" startIcon={<SaveIcon />} onClick={() => saveTemplate(true)} sx={{ ml: 1 }}>
                                                {currentTemplateName ? '別名で保存' : '保存'}
                                            </Button>
                                        </Box>
                                    </Box>

                                    <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
                                        利用可能変数: {'{customer_name}'}, {'{start_date_mmdd}'}, {'{start_date}'}, {'{end_date}'}, {'{sum_price}'}, {'{contact_code}'}, {'{agreement}'} 等
                                        <br />
                                        If文: {'{% if line == 0 %}...{% endif %}'}, {'{% if agreement == 0 %}...{% endif %}'}, {'{% if sum_price > 0 %}...{% endif %}'}
                                    </Typography>

                                    <TextField
                                        fullWidth
                                        label="件名"
                                        variant="outlined"
                                        sx={{ mb: 2 }}
                                        value={template.subject}
                                        onChange={(e) => setTemplate({ ...template, subject: e.target.value })}
                                    />
                                    <TextField
                                        fullWidth
                                        label="本文"
                                        variant="outlined"
                                        multiline
                                        rows={15}
                                        value={template.body}
                                        onChange={(e) => setTemplate({ ...template, body: e.target.value })}
                                        sx={{ bgcolor: '#fff' }}
                                    />

                                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button
                                            variant="contained"
                                            size="large"
                                            color="primary"
                                            startIcon={status.loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                                            onClick={handleSend}
                                            disabled={status.loading}
                                            sx={{ borderRadius: 2, px: 6, py: 1.5, fontWeight: 700 }}
                                        >
                                            {status.loading ? '送信中...' : '一斉送信を開始'}
                                        </Button>
                                    </Box>
                                </Box>
                            ) : (
                                <Box>
                                    <Typography variant="subtitle2" sx={{ mb: 2, color: '#64748b' }}>
                                        1件目の宛先 ({recipients[0]?.customer_name} 様) のプレビュー
                                    </Typography>
                                    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f1f5f9' }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                            Subject: {parseTemplate(template.subject, recipients[0])}
                                        </Typography>
                                        <Divider sx={{ mb: 2 }} />
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                                            {parseTemplate(template.body, recipients[0])}
                                        </Typography>
                                    </Paper>
                                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                                        <Button variant="outlined" onClick={() => setTabValue(0)}>エディタに戻る</Button>
                                    </Box>
                                </Box>
                            )}
                        </Paper>
                    </Grid>
                </Grid>
            </Box>

            {/* 一斉送信進捗通知 */}
            <Snackbar
                open={taskStatus.open}
                autoHideDuration={taskStatus.duration}
                onClose={(e, reason) => {
                    if (reason === 'clickaway') return;
                    setTaskStatus(prev => ({ ...prev, open: false }));
                }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setTaskStatus(prev => ({ ...prev, open: false }))}
                    severity={taskStatus.severity}
                    variant="filled"
                    sx={{ width: '100%', boxShadow: 3 }}
                    icon={taskStatus.sending ? <CircularProgress size={20} color="inherit" /> : undefined}
                >
                    {taskStatus.message}
                </Alert>
            </Snackbar>

            {/* テンプレート選択ダイアログ */}
            <Dialog
                open={templateDialog.open}
                onClose={() => setTemplateDialog(prev => ({ ...prev, open: false }))}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>テンプレートを選択</DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    {templateDialog.loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : (
                        <List>
                            {templateDialog.list.length > 0 ? (
                                templateDialog.list.map((name, idx) => (
                                    <ListItem
                                        {...{button: true} as any}
                                        key={idx}
                                        onClick={() => loadTemplate(name)}
                                    >
                                        <ListItemText primary={name} />
                                    </ListItem>
                                ))
                            ) : (
                                <ListItem>
                                    <ListItemText secondary="保存されたテンプレートはありません" />
                                </ListItem>
                            )}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTemplateDialog(prev => ({ ...prev, open: false }))}>キャンセル</Button>
                </DialogActions>
            </Dialog>

            {/* 名前編集ダイアログ */}
            <Dialog
                open={nameEditModal.open}
                onClose={() => setNameEditModal({ open: false, id: null, name: '' })}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>名前を編集</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        autoFocus
                        margin="dense"
                        label="顧客名"
                        value={nameEditModal.name}
                        onChange={(e) => setNameEditModal({ ...nameEditModal, name: e.target.value })}
                        onKeyPress={(e) => e.key === 'Enter' && updateRecipientName()}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNameEditModal({ open: false, id: null, name: '' })}>キャンセル</Button>
                    <Button onClick={updateRecipientName} variant="contained" color="primary">更新</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default BulkMail;
